import React, { useState } from 'react';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import './VendorPortal.css';
import Footer from '../components/Footer';

function VendorPortal() {
  const [vendorCode, setVendorCode] = useState('');
  const [vendor, setVendor] = useState(null);
  const [orders, setOrders] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);

  const handleLogin = async () => {
    if (!vendorCode.trim()) {
      setError('Please enter a vendor code');
      return;
    }
    setLoading(true);
    setError('');

    try {
      console.log('Searching for code:', vendorCode.trim());
      const vendorsQuery = query(
        collection(db, 'vendors'),
        where('code', '==', vendorCode.trim())
      );
      const vendorSnapshot = await getDocs(vendorsQuery);
      console.log('Results found:', vendorSnapshot.size);
      vendorSnapshot.docs.forEach(d => console.log('Doc data:', d.data()));

      if (vendorSnapshot.empty) {
        setError('Invalid vendor code. Please try again.');
        setLoading(false);
        return;
      }

      const vendorDoc = vendorSnapshot.docs[0];
      const vendorData = vendorDoc.data();
      setVendor({ id: vendorDoc.id, ...vendorData });

      const eventPromises = vendorData.assignedEvents.map(async (eventId) => {
  try {
    const eventDoc = await getDoc(doc(db, 'events', eventId));
    if (eventDoc.exists()) {
      return { id: eventDoc.id, ...eventDoc.data() };
    }
    return null;
  } catch { return null; }
});
const eventList = (await Promise.all(eventPromises)).filter(Boolean);
setEvents(eventList);
if (eventList.length > 0) setSelectedEvent(eventList[0].id);

      const allOrdersSnapshot = await getDocs(collection(db, 'orders'));
      const relevantOrders = allOrdersSnapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .filter((order) =>
          order.tickets?.some((ticket) =>
            eventList.some((event) => event.name === ticket.eventName)
          )
        );
      setOrders(relevantOrders);
    } catch (err) {
      setError(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setVendor(null);
    setVendorCode('');
    setEvents([]);
    setOrders([]);
    setSelectedEvent(null);
    setError('');
  };

  const getOrdersForEvent = (event) =>
    orders.filter((order) =>
      order.tickets?.some((ticket) => ticket.eventName === event.name)
    );

  const getTotalTicketsForEvent = (event) =>
    getOrdersForEvent(event).reduce((sum, order) => {
      return sum + (order.tickets?.filter((t) => t.eventName === event.name)
        .reduce((s, t) => s + (t.quantity || 1), 0) || 0);
    }, 0);

    const getTotalRevenueForEvent = (event) =>
    getOrdersForEvent(event).reduce((sum, order) => {
      return sum + (order.tickets?.filter((t) => t.eventName === event.name)
        .reduce((s, t) => s + ((t.ticketPrice || 0) * (t.quantity || 1)), 0) || 0);
    }, 0);

  const getTicketTypeBreakdown = (event) => {
    const breakdown = {};
    getOrdersForEvent(event).forEach((order) => {
      order.tickets?.filter((t) => t.eventName === event.name).forEach((t) => {
        const key = t.ticketType || 'General';
        if (!breakdown[key]) breakdown[key] = { qty: 0, revenue: 0 };
        breakdown[key].qty += t.quantity || 1;
        breakdown[key].revenue += (t.ticketPrice || 0) * (t.quantity || 1);
      });
    });
    return breakdown;
  };

const downloadCSV = (event) => {
  const eventOrders = [...getOrdersForEvent(event)].sort((a, b) => {
    const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
    const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
    return bTime - aTime;
  });

  const rows = [];
  rows.push(['Order #', 'Date', 'Customer', 'Email', 'Phone', 'Ticket Type', 'Quantity', 'Ticket Number', 'Order Total']);

  eventOrders.forEach((order) => {
    const eventTickets = order.tickets?.filter((t) => t.eventName === event.name) || [];
    const orderTotal = getOrderTotal(order, event);
    const date = order.createdAt?.toDate?.()?.toLocaleDateString() || order.createdAt || 'N/A';

    eventTickets.forEach((ticket, i) => {
      rows.push([
        i === 0 ? order.orderNumber : '',
        i === 0 ? date : '',
        i === 0 ? order.name : '',
        i === 0 ? (order.email || '') : '',
        i === 0 ? (order.phone || '') : '',
        ticket.ticketType || 'General',
        ticket.quantity || 1,
        ticket.ticketNumber || '',
        i === 0 ? `$${orderTotal.toFixed(2)}` : ''
      ]);
    });
  });

  const csv = rows.map(row =>
    row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
  ).join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${event.name.replace(/\s+/g, '_')}_orders.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

  const getOrderTotal = (order, event) =>
    order.tickets?.filter((t) => t.eventName === event.name)
      .reduce((s, t) => s + ((t.ticketPrice || 0) * (t.quantity || 1)), 0) || 0;

  if (loading) {
    return (
      <div className="vp-loading">
        <div className="vp-spinner"></div>
        <p>Verifying vendor code...</p>
      </div>
    );
  }

  if (!vendor) {
    return (
      <div className="vp-login-page">
        <div className="vp-login-card">
          <div className="vp-login-logo">🎟</div>
          <h1>Vendor Portal</h1>
          <p className="vp-login-subtitle">Enter your vendor code to access your orders</p>
          <div className="vp-login-form">
            <input
              type="text"
              placeholder="Enter Vendor Code"
              value={vendorCode}
              onChange={(e) => setVendorCode(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
              className="vp-code-input"
            />
            <button onClick={handleLogin} className="vp-login-btn">
              Access Portal →
            </button>
          </div>
          {error && <p className="vp-error">⚠ {error}</p>}
        </div>
        <Footer />
      </div>
    );
  }

  const activeEvent = events.find((e) => e.id === selectedEvent);

  return (
    <div className="vp-container">
      <div className="vp-topbar">
        <div className="vp-topbar-left">
          <span className="vp-topbar-badge">{vendor.code}</span>
          <span className="vp-topbar-name">Welcome, {vendor.name}</span>
        </div>
        <button onClick={handleLogout} className="vp-logout-btn">Logout</button>
      </div>

      <div className="vp-content">

        {/* Event Tabs */}
        <div className="vp-event-tabs">
          {events.map((event) => (
            <button
              key={event.id}
              className={`vp-event-tab ${selectedEvent === event.id ? 'active' : ''}`}
              onClick={() => setSelectedEvent(event.id)}
            >
              {event.name}
              <span className="vp-tab-count">{getOrdersForEvent(event).length}</span>
            </button>
          ))}
        </div>

        {activeEvent && (
          <>
            {/* Event Summary Card */}
            <div className="vp-event-summary">
              <div className="vp-summary-item">
                <span className="vp-summary-label">Event</span>
                <span className="vp-summary-value">{activeEvent.name}</span>
              </div>
              <div className="vp-summary-item">
                <span className="vp-summary-label">Date & Time</span>
                <span className="vp-summary-value">{activeEvent.datetime || 'TBD'}</span>
              </div>
              <div className="vp-summary-item">
                <span className="vp-summary-label">Location</span>
                <span className="vp-summary-value">{activeEvent.location || 'TBD'}</span>
              </div>
              <div className="vp-summary-item">
                <span className="vp-summary-label">Total Orders</span>
                <span className="vp-summary-value highlight">{getOrdersForEvent(activeEvent).length}</span>
              </div>
              <div className="vp-summary-item">
                <span className="vp-summary-label">Total Tickets</span>
                <span className="vp-summary-value highlight">{getTotalTicketsForEvent(activeEvent)}</span>
              </div>
              <div className="vp-summary-item">
                <span className="vp-summary-label">Total Revenue</span>
                <span className="vp-summary-value highlight">${getTotalRevenueForEvent(activeEvent).toFixed(2)}</span>
              </div>
            </div>

            {/* Ticket Type Breakdown */}
            <div className="vp-ticket-breakdown">
              <h3 className="vp-breakdown-heading">Ticket Breakdown</h3>
              <div className="vp-breakdown-grid">
                {Object.entries(getTicketTypeBreakdown(activeEvent)).map(([type, data]) => (
                  <div key={type} className="vp-breakdown-card">
                    <span className="vp-breakdown-type">{type}</span>
                    <span className="vp-breakdown-qty">{data.qty} tickets</span>
                    <span className="vp-breakdown-revenue">${data.revenue.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Orders */}
            <div className="vp-orders-header">
  <h2 className="vp-orders-heading">Orders</h2>
  <button className="vp-export-btn" onClick={() => downloadCSV(activeEvent)}>
    ⬇ Export CSV
  </button>
</div>
            {getOrdersForEvent(activeEvent).length === 0 ? (
              <div className="vp-empty">No orders found for this event.</div>
            ) : (
              <div className="vp-orders-grid">
                {[...getOrdersForEvent(activeEvent)]
                  .sort((a, b) => {
                    const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
                    const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
                    return bTime - aTime;
                  })
                  .map((order) => (
                  <div key={order.id} className="vp-order-card">
                    <div className="vp-order-header">
                      <span className="vp-order-number">#{order.orderNumber}</span>
                      <span className="vp-order-date">
                        {order.createdAt?.toDate?.()?.toLocaleDateString() || order.createdAt || 'N/A'}
                      </span>
                    </div>
                    <div className="vp-order-body">
                      <div className="vp-order-row">
                        <span className="vp-order-label">Customer</span>
                        <span>{order.name}</span>
                      </div>
                      <div className="vp-order-row">
                        <span className="vp-order-label">Email</span>
                        <span>{order.email || 'N/A'}</span>
                      </div>
                      <div className="vp-order-row">
                        <span className="vp-order-label">Phone</span>
                        <span>{order.phone || 'N/A'}</span>
                      </div>
                    </div>
                    <div className="vp-order-tickets">
                      <p className="vp-tickets-label">Tickets</p>
                      {order.tickets
                        ?.filter((t) => t.eventName === activeEvent.name)
                        .map((ticket, i) => (
                          <div key={i} className="vp-ticket-row">
                            <span>{ticket.ticketType || 'General'}</span>
                            <span className="vp-ticket-qty">x{ticket.quantity || 1}</span>
                            {ticket.ticketNumber && (
                              <span className="vp-ticket-num">#{ticket.ticketNumber}</span>
                            )}
                          </div>
                        ))}
                    </div>
                    <div className="vp-order-footer">
                      <span className="vp-order-total-label">Order Total</span>
                      <span className="vp-order-total">${getOrderTotal(order, activeEvent).toFixed(2)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}

export default VendorPortal;